import { DateField } from "../../components/DateField";
import { useState } from "react";
import {
  Banner,
  Button,
  Checkbox,
  Field,
  PersonCell,
  SelectX,
  StatusPill,
  Textarea,
  Avatar,
} from "@guestpilot/ui-kit";
import { useGet } from "../../api/hooks";
import type { PersonRef, Schema } from "../../api/types";
import { PersonPicker } from "../../components/PersonPicker";
import { Flag, type Participant, type Reason } from "../../components/Participants";
import { useI18n } from "../../i18n";
import { useSession } from "../../session";

export type Decision = Schema<"Decision">;
type Kind = Decision["decision"];
type Settings = Schema<"Settings">;
type Preview = {
  outcome: string;
  executeAfterHours?: number;
  mode?: string;
  otherControlledTeams?: Schema<"TeamRef">[];
  activeExceptionWillBeRevoked?: boolean;
  warnsOutsideScope?: boolean;
};

export const KINDS: Kind[] = ["keep", "extend", "remove", "exception", "transfer_sponsor"];

const today = () => new Date().toISOString().slice(0, 10);

/** Client-side check of the per-decision rules (BR-081…BR-086); the API validates again and its field errors are shown (BR-211). */
export function validateDecision(
  kind: Kind,
  f: {
    sponsor: string | null;
    expiry: string;
    reason: string;
    reasonText: string;
    until: string;
    currentSponsor: string | null;
    currentExpiry: string | null;
  },
  maxAccessDays: number | null,
  now = today(),
): Record<string, string> {
  const e: Record<string, string> = {};
  const futureExpiry = (): void => {
    if (!f.expiry) e["expiryDate"] = "required";
    else if (f.expiry <= now) e["expiryDate"] = "future";
    else if (
      maxAccessDays &&
      f.expiry > new Date(Date.parse(now) + maxAccessDays * 86_400_000).toISOString().slice(0, 10)
    )
      e["expiryDate"] = "max";
  };
  if (kind === "keep") {
    if (!f.sponsor) e["sponsorPersonId"] = "required";
    futureExpiry();
    if (!f.reason) e["accessReasonId"] = "required";
  }
  if (kind === "extend") {
    futureExpiry();
    if (!e["expiryDate"] && f.currentExpiry && f.expiry <= f.currentExpiry) e["expiryDate"] = "later";
    if (!f.reason) e["accessReasonId"] = "required";
  }
  if (kind === "remove" && !f.reasonText.trim()) e["reasonText"] = "required";
  if (kind === "exception") {
    if (!f.reasonText.trim()) e["reasonText"] = "required";
    if (!f.until || f.until <= now) e["exceptionValidUntil"] = f.until ? "future" : "required";
  }
  if (kind === "transfer_sponsor" && (!f.sponsor || f.sponsor === f.currentSponsor))
    e["sponsorPersonId"] = f.sponsor ? "different" : "required";
  return e;
}

/** One guest's review form: risk flags, sponsor, expiry, reason, notes and the five decision buttons (team-tab.md Detail). */
/** An Admin's own Remove decision counts as validation (BR-210): it is scheduled like a delegated request, it does not wait for another Admin. */
function removeOutcome(outcome: string | undefined, hours: number | undefined, isAdmin: boolean): string {
  if (outcome !== "needs_admin_validation" || !isAdmin) return outcome ?? "record_only";
  return (hours ?? 0) > 0 ? "scheduled_after_delay" : "executes_immediately";
}

export function DecisionForm({
  participant,
  itemVersion,
  participantVersion,
  decided,
  serverErrors,
  busy,
  onSubmit,
  gateReason,
}: {
  participant: Participant;
  itemVersion?: number | undefined;
  participantVersion?: number | undefined;
  decided?: string | undefined;
  serverErrors?: Record<string, string> | undefined;
  busy?: boolean | undefined;
  onSubmit: (d: Decision & { participantVersion?: number }) => void;
  gateReason?: string | undefined;
}) {
  const { t } = useI18n();
  const s = useSession();
  const reasons = useGet<Reason[]>("/api/v1/access-reasons");
  const settings = useGet<Settings>("/api/v1/settings", undefined, { enabled: s.can("SETTINGS_READ") });
  const [sponsor, setSponsor] = useState<PersonRef | null>(participant.sponsor ?? null);
  const [expiry, setExpiry] = useState(participant.expiryDate ?? "");
  const [reason, setReason] = useState(participant.reason?.id ?? "");
  const [reasonText, setReasonText] = useState("");
  const [comment, setComment] = useState("");
  const [until, setUntil] = useState("");
  const [removeFor, setRemoveFor] = useState<boolean>(false);
  const [confirmed, setConfirmed] = useState(false);
  const [kind, setKind] = useState<Kind | null>(null);
  const [touched, setTouched] = useState(false);
  const preview = useGet<Preview>(`/api/v1/participants/${participant.id}/removal-preview`, undefined, {
    enabled: removeFor,
  });
  const maxAccess = settings.data?.maxAccessDays ?? null;
  const values = {
    sponsor: sponsor?.id ?? null,
    expiry,
    reason,
    reasonText,
    until,
    currentSponsor: participant.sponsor?.id ?? null,
    currentExpiry: participant.expiryDate ?? null,
  };
  const local = kind ? validateDecision(kind, values, maxAccess) : {};
  const err = (field: string): string | undefined => {
    const server = serverErrors?.[field];
    if (server) return server;
    const code = touched ? local[field] : undefined;
    return code ? t(`ui:decision.err.${code}`, { days: maxAccess ?? 0 }) : undefined;
  };
  const reasonOptions = [
    { value: "", label: t("ui:users.noReason") },
    ...(reasons.data ?? [])
      .filter((r) => !r.retired || r.id === participant.reason?.id)
      .map((r) => ({ value: r.id ?? "", label: r.label ?? "" })),
  ];
  const irreversible =
    preview.data?.outcome === "executes_immediately" || preview.data?.outcome === "scheduled_after_delay";

  const submit = (k: Kind) => {
    setKind(k);
    setTouched(true);
    const errs = validateDecision(k, values, maxAccess);
    if (Object.keys(errs).length > 0) return;
    onSubmit({
      decision: k,
      itemVersion: itemVersion ?? 0,
      ...(participantVersion !== undefined ? { participantVersion } : {}),
      ...(k === "keep" || k === "transfer_sponsor" ? { sponsorPersonId: sponsor?.id } : {}),
      ...(k === "keep" || k === "extend" ? { expiryDate: expiry, accessReasonId: reason } : {}),
      ...(k === "remove" || k === "exception" ? { reasonText } : {}),
      ...(k === "exception" ? { exceptionValidUntil: until } : {}),
      ...(comment ? { comment } : {}),
      ...(k === "remove" && irreversible ? { confirmIrreversible: true } : {}),
    });
  };

  return (
    <div className="gp-stack" style={{ gap: "var(--space-16)" }}>
      <div className="gp-row">
        <Avatar name={participant.displayName || participant.email} size={40} />
        <div className="gp-person-text">
          <span className="t-title-sm">{participant.displayName || participant.email}</span>
          <span className="t-body gp-muted">
            {participant.email} · {participant.domain}
          </span>
        </div>
        {decided ? <StatusPill domain="outcome" value={decided} label={t(`ui:outcome.${decided}`)} /> : null}
      </div>
      <div className="gp-row" aria-label={t("ui:home.filterRisk")}>
        {(participant.risks ?? [])
          .filter((r) => r.type && !r.excepted)
          .map((r) => (
            <Flag key={r.type} risk={r.type!} />
          ))}
        {participant.suggestedSponsor && !participant.sponsor ? (
          <span className="t-body-sm gp-muted">
            {t("ui:users.suggested", { name: participant.suggestedSponsor.displayName })}
          </span>
        ) : null}
      </div>
      {participant.channels && participant.channels.length > 0 ? (
        <p className="t-body-sm gp-muted" style={{ margin: 0 }}>
          {t("ui:decision.channels", { list: participant.channels.map((c) => c.name).join(", ") })}
        </p>
      ) : null}
      <Field label={t("ui:users.col.sponsor")} error={err("sponsorPersonId")}>
        <PersonPicker label={t("ui:users.col.sponsor")} value={sponsor} onChange={setSponsor} />
      </Field>
      <div className="gp-grid-2">
        <Field
          label={t("ui:users.col.expiry")}
          error={err("expiryDate")}
          help={maxAccess ? t("ui:decision.maxAccess", { days: maxAccess }) : undefined}
        >
          <DateField value={expiry} onChange={(e) => setExpiry(e.target.value)} />
        </Field>
        <Field label={t("ui:users.col.reason")} error={err("accessReasonId")}>
          <SelectX options={reasonOptions} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
      <Field
        label={t("ui:decision.reasonText")}
        error={err("reasonText")}
        help={t("ui:decision.reasonTextHelp")}
      >
        <Textarea value={reasonText} onChange={(e) => setReasonText(e.target.value)} />
      </Field>
      <Field
        label={t("ui:decision.until")}
        error={err("exceptionValidUntil")}
        help={t("ui:decision.untilHelp")}
      >
        <DateField value={until} onChange={(e) => setUntil(e.target.value)} />
      </Field>
      <Field label={t("ui:users.notes")}>
        <Textarea value={comment} onChange={(e) => setComment(e.target.value)} />
      </Field>
      {removeFor && preview.data ? (
        <div className="gp-stack">
          <Banner tone={irreversible ? "warn" : "info"}>
            {t(
              `ui:removal.outcome.${removeOutcome(preview.data.outcome, preview.data.executeAfterHours, s.can("REMOVAL_VALIDATE"))}`,
              {
                hours: preview.data.executeAfterHours ?? 0,
              },
            )}
          </Banner>
          {preview.data.mode === "entra_account" || preview.data.warnsOutsideScope ? (
            <Banner tone="warn">{t("ui:removal.outsideScope")}</Banner>
          ) : null}
          {(preview.data.otherControlledTeams ?? []).length > 0 ? (
            <Banner tone="warn">
              {t("ui:removal.otherTeams", {
                teams: (preview.data.otherControlledTeams ?? []).map((x) => x.displayName).join(", "),
              })}
            </Banner>
          ) : null}
          {preview.data.activeExceptionWillBeRevoked ? (
            <Banner tone="warn">{t("ui:removal.exceptionRevoked")}</Banner>
          ) : null}
          {irreversible ? (
            <Checkbox checked={confirmed} onChange={setConfirmed} label={t("ui:removal.irreversible")} />
          ) : null}
        </div>
      ) : null}
      <div className="gp-decisions" role="group" aria-label={t("ui:decision.group")}>
        {KINDS.map((k) => {
          const isRemove = k === "remove";
          const needConfirm = isRemove && removeFor && irreversible && !confirmed;
          return (
            <Button
              key={k}
              variant={k === "keep" ? "primary" : isRemove ? "danger" : "default"}
              disabledReason={
                gateReason ??
                (busy ? t("common:loading") : needConfirm ? t("ui:removal.confirmRequired") : undefined)
              }
              onClick={() => {
                if (isRemove && !removeFor) {
                  setKind("remove");
                  setRemoveFor(true);
                  setTouched(true);
                  return;
                }
                submit(k);
              }}
            >
              {/* the first click on Remove only shows what will happen: the second one, named so, sends the request */}
              {isRemove && removeFor ? t("ui:decision.confirmRemove") : t(`ui:decision.${k}`)}
            </Button>
          );
        })}
      </div>
      {participant.sponsor ? (
        <PersonCell
          name={participant.sponsor.displayName}
          sub={t("ui:decision.currentSponsor")}
          avatar={false}
        />
      ) : null}
    </div>
  );
}
